<template>
  <!-- Payment method select -->
  <div class="select-payout-type" redesign>
    <RequestStepProgressBar v-if="!reproRightsRequest && !isAddPaymentMethod" isPaypalVerify />

    <div class="local-hero" v-if="isAddPaymentMethod">
      <h1 class="local-hero-header" v-translate>Add Payment Method</h1>
    </div>
    
    <template v-if="showPaymentSelect">
      <div class="select-payout-type-form form-container">
        <div class="select-payout-type-form-header form-header" v-translate>
          Select how you would like to receive payment:
        </div>
        <div class="payout-types">
          <div v-if="payPalEnabled" class="paypal-card card-shape">
            <img class="payout-type-logo" :src="require('@/assets/images/paypal-logo.png')" alt="PayPal" />
            <p v-translate>
              If you woud like receive your payment via a bank account in the USA please use a PayPal account for your payment method.
            </p>
            <button class="paypal-button form-button" @click="setSelectedPaymentMethod('PAYPAL')" data-action="select-paypal" v-translate>Add PayPal Email</button>
          </div>
          <div v-if="wiseEnabled" class="wise-card card-shape">
            <img class="payout-type-logo" :src="require('@/assets/images/wise-logo.png')" alt="Wise" />
            <p v-translate>
              If your bank account is not in the USA, please select this option to provide us your account details.
            </p>
            <button class="wise-button form-button" @click="setSelectedPaymentMethod('WISE')" data-action="select-wise" v-translate>Add Account Information</button>
          </div>
        </div>
        <div class="form-footer" v-if="!isAddPaymentMethod">
          <span>OR</span>
          <SubmitButton :buttonText="$gettext('Skip For Now')" :darkButton="true" />
        </div>
      </div>
    </template>

    <!-- Paypal -->
    <PayPalVerify v-if="isPayPal" @go-back="handleReselectPayoutMethod" :isAddPaymentMethod="isAddPaymentMethod" :multiplePayoutMethods="multiplePayouts" />

    <!-- Wise -->
    <WiseForm v-else-if="isWise" @go-back="handleReselectPayoutMethod" :isAddPaymentMethod="isAddPaymentMethod" :multiplePayoutMethods="multiplePayouts" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import Request from '@/services/requests'
import PayPalVerify from '@/views/PayPalVerify'
import WiseForm from '@/views/WiseForms'
import SubmitButton from '@/components/requests/SubmitButton'
import Employee from '@/services/employees'

export default {
  name: 'ReceiveMoney',
  components: {
    RequestStepProgressBar,
    PayPalVerify,
    WiseForm,
    SubmitButton,
  },
  data() {
    return {
      selectedPaymentMethod: null,
    }
  },
  props: {
    isAddPaymentMethod: {
      type: Boolean,
      required: false,
    },
  },
  mounted() {
    if(this.user.paypalEmail) {
      this.selectedPaymentMethod = 'PAYPAL'
      return
    }

    if(this.user.wiseAccountId) {
      this.selectedPaymentMethod = 'WISE'
      return
    }
  },
  methods: {
    async setSelectedPaymentMethod(type) {
      this.selectedPaymentMethod = type

      if(this.isAddPaymentMethod) {
        this.user.paypalEmail = ''
        this.user.wiseAccountId = ''
        return
      }

      this.currentRequest.selectedPayoutType = type
      const response = await Request.api.update(this.currentRequest, ['selectedPayoutType'])
      this.$store.dispatch('updateCurrentRequest', response)
    },
    async handleReselectPayoutMethod() {
      //let initalPaymentMethod = this.selectedPaymentMethod
      if(this.isAddPaymentMethod && this.userHasPaymentMethod) {
        let userCopy = this.user
        if(userCopy.paypalEmail && this.selectedPaymentMethod == 'PAYPAL')
          userCopy.paypalEmail = ''
        if(userCopy.wiseAccountId && this.selectedPaymentMethod == 'WISE')
          userCopy.wiseAccountId = ''

        try {
          const response = await Employee.api.update(userCopy, ['paypalEmail', 'wiseAccountId'])
          this.$store.dispatch('updateUser', Employee.fromAPI(response))
          this.selectedPaymentMethod = null
        } catch {
          // Do nothing
        }
        
        return
      }
      
      this.currentRequest.selectedPayoutType = null
      const response = await Request.api.update(this.currentRequest, ['selectedPayoutType'])
      this.$store.dispatch('updateCurrentRequest', response)
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'currentRequest']),
    isPayPal() {
      if(this.selectedPaymentMethod == 'PAYPAL')
        return true

      if(this.currentRequest && this.currentRequest.selectedPayoutType === 'PAYPAL')
        return true

      if(this.onlyPayPal)
        return true

      return false
    },
    isWise() {
      if(this.selectedPaymentMethod == 'WISE')
        return true

      if(this.currentRequest && this.currentRequest.selectedPayoutType === 'WISE')
        return true

      if(this.onlyWise)
        return true

      return false
    },
    multiplePayouts() {
      return this.organizationDetails.payoutTypes.length > 1
    },
    showPaymentSelect() {
      return (this.organizationDetails.payoutTypes.length > 1 && !this.selectedPaymentMethod && !this.isPayPal && !this.isWise)
    },
    onlyPayPal() {
      return (this.organizationDetails.payoutTypes && this.organizationDetails.payoutTypes.includes('PAYPAL') && this.organizationDetails.payoutTypes.length === 1)
    },
    onlyWise() {
      return (this.organizationDetails.payoutTypes && this.organizationDetails.payoutTypes.includes('WISE') && this.organizationDetails.payoutTypes.length === 1)
    },
    payPalEnabled() {
      return this.organizationDetails.payoutTypes.includes('PAYPAL')
    },
    wiseEnabled() {
      return this.organizationDetails.payoutTypes.includes('WISE')
    },
    userHasPaymentMethod() {
      if(!this.user)
        return false

      if(this.user.paypalEmail || this.user.wiseAccountId)
        return true

      return false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.select-payout-type {
  display: flex;
  flex-direction: column;

  .select-payout-type-form {
    @extend .constrain-tablet-and-up;

    .select-payout-type-form-header {
      font-size: 1.4rem;
      text-align: center;
    }
  }

  .payout-types {
    display: flex;
    flex-direction: column;

    @include tablet-and-up-screen {
      flex-direction: row;
    }

    .card-shape {
      justify-content: space-between;
      margin: 1rem 0;

      &:first-of-type {
        margin-top: 0;
      }

      &:last-of-type {
        margin-bottom: 0;
      }

      @include tablet-and-up-screen {
        margin: 0 1rem;
        width: 50%;

        &:first-of-type {
          margin-left: 0;
        }

        &:last-of-type {
          margin-right: 0;
        }
      }
    }
  }

  .paypal-card, .wise-card {
    border: 2px solid transparent;
    display: flex;
    flex-direction: column;

    &:hover {
      border: 2px solid black;
    }

    img {
      align-self: center;
      height: 35px;
      width: auto;
    }

    p {
      align-self: center;
      border-top: 1px dashed black;
      margin-top: 1rem;
      margin-bottom: 0rem;
      padding-top: 1rem;
      padding-bottom: 1rem;
      width: 100%;
      max-width: 450px;

      @include tablet-screen {
        height: 10rem;
        width: 100%;
        max-width: 550px;
      }

      @include desktop-screen {
        height: 10rem;
        width: 80%;
        max-width: 550px;
      }
    }

    .form-button {
      align-self: center;
      width: 100%;
      max-width: 450px;
      margin-top: 1rem;

      @include tablet-screen {
        width: 100%;
        max-width: 550px;
        margin-top: 0;
      }

      @include desktop-screen {
        width: 80%;
        max-width: 550px;
        margin-top: 0;
      }
    }
  }

  .paypal-card {
    background-color: #a0bed7;

    &:hover {
      border-color: #4a7a9c;
    }

    .paypal-button {
      background-color: #4a7a9c;
    }
  }

  .wise-card {
    background-color: #bbbbbb;

    &:hover {
      border-color: #454545;
    }

    .wise-button {
      background-color: #454545;
    }
  }

  .form-footer {
    text-align:center;

    span {
      display: block;
      font-size: 1.8rem;
      font-weight: bold;
      margin: 1rem 0;
    }

    .form-button {
      padding: 2rem;
    }
  }
}
</style>
